import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authStorage } from '../../utils/storage';
import { CheckCircle, History, RefreshCw, Search, Download } from 'lucide-react';
import MembershipAuditModal from '../units/MembershipAuditModal';
import PageHead from '../general/PageHead';
import './MembershipAuditsPage.css';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'recent', label: 'Recent (30d)' },
  { key: 'decert', label: 'Decert' },
  { key: 'missing', label: 'No Audit' }
];

const isDecertValue = (value) => String(value || '').trim().toLowerCase().startsWith('y');

const formatLocalCode = (localNumber, unitNumber) => {
  const local = localNumber != null ? String(localNumber).padStart(4, '0') : '';
  const unit = unitNumber != null ? String(unitNumber).padStart(4, '0') : '';
  if (local && unit) return `${local}-${unit}`;
  return local || unit || '-';
};

const daysSince = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  const diff = Date.now() - date.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const getAuditStatus = (auditDate) => {
  const ageDays = daysSince(auditDate);
  if (!auditDate || ageDays === null) return 'missing';
  if (ageDays > 121) return 'overdue';
  if (ageDays <= 30) return 'recent';
  return 'ok';
};

// % of members paying dues; null when either side is unusable.
const pctPaying = (row) => {
  const total = Number(row.total_members);
  const paying = Number(row.paying_dues);
  if (!Number.isFinite(total) || total <= 0 || row.paying_dues === null || row.paying_dues === undefined || !Number.isFinite(paying)) return null;
  return Math.round((paying / total) * 100);
};

const SORT_ACCESSORS = {
  local: (r) => (Number(r.local_number) || 0) * 100000 + (Number(r.unit_number) || 0),
  pct: (r) => pctPaying(r) ?? -1,
  name: (r) => (r.local_name || '').toLowerCase(),
  town: (r) => (r.town || '').toLowerCase(),
  audit_date: (r) => (r.audit_date ? new Date(r.audit_date).getTime() : 0),
  signed_by: (r) => (r.audited_by_name || '').toLowerCase(),
  total_members: (r) => r.total_members ?? -1,
  paying_dues: (r) => r.paying_dues ?? -1,
  no_card_no_dues: (r) => r.no_card_no_dues ?? -1,
  dues_no_card: (r) => r.dues_no_card ?? -1,
  opt_outs: (r) => r.opt_outs ?? -1,
  refusals: (r) => r.refusals ?? -1,
  card_no_dues: (r) => r.card_no_dues ?? -1,
  dues_with_card: (r) => r.dues_with_card ?? -1,
  phones_on_file: (r) => r.phones_on_file ?? -1,
  emails_on_file: (r) => r.emails_on_file ?? -1
};

// Decert locals are tracked separately and never counted as overdue.
const getRowStatus = (row) => (isDecertValue(row.decert) ? 'decert' : getAuditStatus(row.audit_date));

const getDelta = (currentValue, previousValue, hasPrevious) => {
  if (!hasPrevious) return null;
  if (currentValue === null || currentValue === undefined || currentValue === '') return null;
  const current = Number(currentValue);
  const previous =
    previousValue === null || previousValue === undefined || previousValue === ''
      ? 0
      : Number(previousValue);
  if (Number.isNaN(current) || Number.isNaN(previous)) return null;
  const delta = current - previous;
  return delta === 0 ? null : delta;
};

const renderMetric = (value, previousValue, hasPrevious) => {
  const delta = getDelta(value, previousValue, hasPrevious);
  return (
    <div className="audit-metric">
      <div>{value ?? '-'}</div>
      {delta !== null && (
        <div className={`audit-delta ${delta > 0 ? 'positive' : 'negative'}`}>
          {delta > 0 ? `+${delta}` : delta}
        </div>
      )}
    </div>
  );
};

const MembershipAuditsPage = () => {
  const token = authStorage.getToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterKey, setFilterKey] = useState('all');
  const [query, setQuery] = useState('');
  const [historyLocal, setHistoryLocal] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [signingOffId, setSigningOffId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sort, setSort] = useState({ key: 'local', dir: 'asc' });

  const toggleSort = (key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/membership-audits/overview', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
    } catch (e) {
      setError('Failed to load membership audits.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  useEffect(() => {
    if (token) fetchOverview();
  }, [token, fetchOverview]);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const res = await fetch('/api/membership-audits/export?count=5', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        setError('Your session has expired — please log out and back in, then export again.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `membership_audits_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError('Failed to export audits.');
    } finally {
      setExporting(false);
    }
  };

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      const status = getRowStatus(row);
      if (filterKey === 'overdue' && status !== 'overdue') return false;
      if (filterKey === 'recent' && status !== 'recent') return false;
      if (filterKey === 'decert' && status !== 'decert') return false;
      if (filterKey === 'missing' && status !== 'missing') return false;
      if (!q) return true;
      const localCode = formatLocalCode(row.local_number, row.unit_number).toLowerCase();
      const name = (row.local_name || '').toLowerCase();
      const town = (row.town || '').toLowerCase();
      return localCode.includes(q) || name.includes(q) || town.includes(q);
    });
    const accessor = SORT_ACCESSORS[sort.key] || SORT_ACCESSORS.local;
    const factor = sort.dir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
  }, [rows, filterKey, query, sort]);

  const handleSignOff = async (auditId) => {
    if (!auditId) return;
    setSigningOffId(auditId);
    setError('');
    try {
      const res = await fetch(`/api/membership-audits/${auditId}/sign-off`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchOverview();
    } catch (e) {
      setError('Failed to sign off audit.');
    } finally {
      setSigningOffId(null);
    }
  };

  const renderMetricInline = (label, value, previousValue, hasPrevious) => {
    const delta = getDelta(value, previousValue, hasPrevious);
    if (value === null || value === undefined || value === '') return null;
    return (
      <div className="ma-card-metric">
        <span className="ma-card-metric-label">{label}</span>
        <span className="ma-card-metric-value">
          {value}
          {delta !== null && (
            <span className={`audit-delta ${delta > 0 ? 'positive' : 'negative'}`}>
              {delta > 0 ? `+${delta}` : delta}
            </span>
          )}
        </span>
      </div>
    );
  };

  const renderMobileCards = () => {
    if (loading) return <div className="audits-loading">Loading audits…</div>;
    if (filteredRows.length === 0) return <div className="audits-loading">No audits found.</div>;

    return (
      <div className="ma-cards">
        {filteredRows.map((row) => {
          const status = getRowStatus(row);
          const hasPrevious = Boolean(row.prev_audit_date);
          return (
            <div key={row.local_id} className="ma-card">
              <div className="ma-card-top">
                <div className="ma-card-id">{formatLocalCode(row.local_number, row.unit_number)}</div>
                <span className={`audit-pill ${status}`}>
                  {status === 'decert' ? 'Decert' : row.audit_date ? new Date(row.audit_date).toLocaleDateString() : 'No audit'}
                </span>
              </div>
              <div className="ma-card-name">
                {row.local_name || '-'}
                {pctPaying(row) !== null && (
                  <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, color: 'var(--accent, #2563eb)' }}>{pctPaying(row)}% paying</span>
                )}
              </div>
              {row.town && <div className="ma-card-town">{row.town}</div>}
              <div className="ma-card-metrics">
                {renderMetricInline('Members', row.total_members, row.prev_total_members, hasPrevious)}
                {renderMetricInline('Paying', row.paying_dues, row.prev_paying_dues, hasPrevious)}
                {renderMetricInline('Opt Outs', row.opt_outs, row.prev_opt_outs, hasPrevious)}
                {renderMetricInline('Refusals', row.refusals, row.prev_refusals, hasPrevious)}
                {renderMetricInline('No Card/No Dues', row.no_card_no_dues, row.prev_no_card_no_dues, hasPrevious)}
                {renderMetricInline('Dues/No Card', row.dues_no_card, row.prev_dues_no_card, hasPrevious)}
                {renderMetricInline('Card/No Dues', row.card_no_dues, row.prev_card_no_dues, hasPrevious)}
                {renderMetricInline('Dues w/ Card', row.dues_with_card, row.prev_dues_with_card, hasPrevious)}
                {renderMetricInline('Phone #s', row.phones_on_file, row.prev_phones_on_file, hasPrevious)}
                {renderMetricInline('Emails', row.emails_on_file, row.prev_emails_on_file, hasPrevious)}
              </div>
              {row.audited_by_name && <div className="ma-card-signedby">Signed off by {row.audited_by_name}</div>}
              <div className="ma-card-actions">
                {row.audit_id ? (
                  row.audited_by_user_id ? (
                    <span className="audit-signed"><CheckCircle size={14} /> Signed</span>
                  ) : (
                    <button
                      className="ma-card-btn primary"
                      onClick={() => handleSignOff(row.audit_id)}
                      disabled={signingOffId === row.audit_id}
                    >
                      <CheckCircle size={14} />
                      {signingOffId === row.audit_id ? 'Signing…' : 'Sign Off'}
                    </button>
                  )
                ) : (
                  <span className="audit-missing">No data</span>
                )}
                <button className="ma-card-btn secondary" onClick={() => setHistoryLocal(row)}>
                  <History size={14} /> History
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <PageHead
        title="Membership Audits"
        sub="Master audit tracking across all locals"
        actions={
          <>
            <button className="btn btn-secondary" onClick={fetchOverview} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
              <Download size={14} /> {exporting ? 'Exporting…' : 'Export XLSX'}
            </button>
          </>
        }
      />

      <div className="list-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'nowrap' }}>
        <div className="pills" style={{ flexShrink: 0 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`pill ${filterKey === f.key ? 'active' : ''}`}
              onClick={() => setFilterKey(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="list-toolbar-right" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span style={{ fontSize: 12.5, color: 'var(--fg-3)', whiteSpace: 'nowrap' }}>
            {filteredRows.length} locals{lastUpdated ? ` · ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
          <div className="search-box">
            <Search size={14} />
            <input
              type="text"
              value={query}
              placeholder="Search by local, name, or town"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {!isMobile && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span style={{ fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>Legend</span>
          <span className="audit-pill recent">Recent</span><span style={{ color: 'var(--fg-2)' }}>within 30 days</span>
          <span className="audit-pill overdue">Overdue</span><span style={{ color: 'var(--fg-2)' }}>older than 121 days</span>
          <span className="audit-pill decert">Decert</span><span style={{ color: 'var(--fg-2)' }}>decertified local (not counted overdue)</span>
          <span className="audit-pill missing">Missing</span><span style={{ color: 'var(--fg-2)' }}>no audit date</span>
          <span className="audit-delta positive">+5</span><span style={{ color: 'var(--fg-2)' }}>increase vs prior</span>
          <span className="audit-delta negative">-3</span><span style={{ color: 'var(--fg-2)' }}>decrease vs prior</span>
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 6, background: 'var(--color-danger-soft)', color: '#8a2d2d', marginBottom: 12, fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {isMobile ? renderMobileCards() : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl ma-audit-fit">
              <colgroup>
                <col style={{ width: 104 }} /><col style={{ width: 46 }} /><col style={{ width: 122 }} /><col style={{ width: 70 }} /><col style={{ width: 74 }} /><col style={{ width: 76 }} />
                <col style={{ width: 52 }} /><col style={{ width: 52 }} /><col style={{ width: 58 }} /><col style={{ width: 56 }} /><col style={{ width: 50 }} /><col style={{ width: 52 }} /><col style={{ width: 58 }} /><col style={{ width: 58 }} />
                <col style={{ width: 52 }} /><col style={{ width: 52 }} />
                <col style={{ width: 64 }} /><col style={{ width: 62 }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { k: 'local', label: 'Local' },
                    { k: 'pct', label: '% Paying', num: true },
                    { k: 'name', label: 'Local Name' },
                    { k: 'town', label: 'Town' },
                    { k: 'audit_date', label: 'Audit Date' },
                    { k: 'signed_by', label: 'Signed Off By' },
                    { k: 'total_members', label: 'Total Members', num: true },
                    { k: 'paying_dues', label: 'Paying Dues', num: true },
                    { k: 'no_card_no_dues', label: 'No Card / No Dues', num: true },
                    { k: 'dues_no_card', label: 'Dues / No Card', num: true },
                    { k: 'opt_outs', label: 'Opt Outs', num: true },
                    { k: 'refusals', label: 'Refusals', num: true },
                    { k: 'card_no_dues', label: 'Card / No Dues', num: true },
                    { k: 'dues_with_card', label: 'Dues w/ Card', num: true },
                    { k: 'phones_on_file', label: 'Phone #s', num: true },
                    { k: 'emails_on_file', label: 'Emails', num: true }
                  ].map((col) => (
                    <th
                      key={col.k}
                      className={col.num ? 'ma-num' : undefined}
                      onClick={() => toggleSort(col.k)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title={`Sort by ${col.label}`}
                    >
                      {col.label}{sort.key === col.k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                  <th>Sign Off</th>
                  <th>History</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={18} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-3)' }}>Loading audits…</td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={18} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-3)' }}>No audits found.</td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const status = getRowStatus(row);
                    const hasPrevious = Boolean(row.prev_audit_date);
                    return (
                      <tr key={row.local_id}>
                        <td className="card-title" data-label="Local" title={formatLocalCode(row.local_number, row.unit_number)} style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, whiteSpace: 'normal', wordBreak: 'break-all', lineHeight: 1.25 }}>
                          {formatLocalCode(row.local_number, row.unit_number)}
                        </td>
                        <td className="ma-num" data-label="% Paying" style={{ fontWeight: 600 }}>
                          {pctPaying(row) !== null ? `${pctPaying(row)}%` : '-'}
                        </td>
                        <td data-label="Local Name" className="ma-ellip" title={row.local_name || ''}>
                          {status === 'decert' && <span className="audit-pill decert" style={{ marginRight: 6, fontSize: 10, padding: '2px 6px' }}>Decert</span>}
                          {row.local_name || '-'}
                        </td>
                        <td data-label="Town" className="ma-ellip" title={row.town || ''}>{row.town || '-'}</td>
                        <td data-label="Audit Date" style={{ whiteSpace: 'nowrap' }}>
                          {status === 'missing'
                            ? <span className="badge badge-danger">No audit</span>
                            : row.audit_date
                              ? <span style={{ fontSize: 12, color: status === 'overdue' ? 'var(--color-danger)' : status === 'recent' ? 'var(--color-success)' : 'var(--fg-2)' }}>
                                  {new Date(row.audit_date).toLocaleDateString()}
                                </span>
                              : <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>—</span>
                          }
                        </td>
                        <td data-label="Signed Off By" className="ma-ellip" title={row.audited_by_name || ''}>{row.audited_by_name || '-'}</td>
                        <td className="ma-num" data-label="Total Members">{renderMetric(row.total_members, row.prev_total_members, hasPrevious)}</td>
                        <td className="ma-num" data-label="Paying Dues">{renderMetric(row.paying_dues, row.prev_paying_dues, hasPrevious)}</td>
                        <td className="ma-num" data-label="No Card / No Dues">{renderMetric(row.no_card_no_dues, row.prev_no_card_no_dues, hasPrevious)}</td>
                        <td className="ma-num" data-label="Dues / No Card">{renderMetric(row.dues_no_card, row.prev_dues_no_card, hasPrevious)}</td>
                        <td className="ma-num" data-label="Opt Outs">{renderMetric(row.opt_outs, row.prev_opt_outs, hasPrevious)}</td>
                        <td className="ma-num" data-label="Refusals">{renderMetric(row.refusals, row.prev_refusals, hasPrevious)}</td>
                        <td className="ma-num" data-label="Card / No Dues">{renderMetric(row.card_no_dues, row.prev_card_no_dues, hasPrevious)}</td>
                        <td className="ma-num" data-label="Dues w/ Card">{renderMetric(row.dues_with_card, row.prev_dues_with_card, hasPrevious)}</td>
                        <td className="ma-num" data-label="Phone #s">{renderMetric(row.phones_on_file, row.prev_phones_on_file, hasPrevious)}</td>
                        <td className="ma-num" data-label="Emails">{renderMetric(row.emails_on_file, row.prev_emails_on_file, hasPrevious)}</td>
                        <td data-label="Sign Off" className="ma-act">
                          {row.audit_id ? (
                            row.audited_by_user_id ? (
                              <span className="audit-signed" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--color-success)' }}>
                                <CheckCircle size={12} /> Signed
                              </span>
                            ) : (
                              <button
                                className="btn btn-primary"
                                onClick={() => handleSignOff(row.audit_id)}
                                disabled={signingOffId === row.audit_id}
                              >
                                {signingOffId === row.audit_id ? '…' : 'Sign Off'}
                              </button>
                            )
                          ) : (
                            <span className="audit-missing" style={{ fontSize: 11, color: 'var(--fg-3)' }}>—</span>
                          )}
                        </td>
                        <td data-label="History" className="ma-act">
                          <button
                            className="btn btn-secondary"
                            onClick={() => setHistoryLocal(row)}
                          >
                            <History size={12} /> History
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {historyLocal && (
        <MembershipAuditModal
          unitId={historyLocal.local_id}
          localLabel={`${formatLocalCode(historyLocal.local_number, historyLocal.unit_number)} · ${historyLocal.local_name || ''}`}
          decert={isDecertValue(historyLocal.decert)}
          onDecertChange={fetchOverview}
          onClose={() => setHistoryLocal(null)}
        />
      )}
    </>
  );
};

export default MembershipAuditsPage;
